import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Database, X, Check, Loader2, Table as TableIcon } from 'lucide-react';
import { interactiveFetch, apiFetch } from '@/api';

const quoteSqlIdentifier = (name: string) => `"${String(name || '').replace(/"/g, '""')}"`;

export const DataModelSelectionModal = ({ isOpen, onClose, currentModel, onConfirm }: any) => {
    const [models, setModels] = useState<any[]>([]);
    const [loadingModels, setLoadingModels] = useState(false);
    const [search, setSearch] = useState('');
    
    const [selectedModel, setSelectedModel] = useState<any>(currentModel);
    const [selectedTable, setSelectedTable] = useState<string | null>(null);
    
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setSelectedModel(currentModel);
            setSelectedTable(null);
            setPreviewData([]);
            setPreviewError(null);
            
            setLoadingModels(true);
            apiFetch('/datamodels')
                .then((d: any) => setModels(Array.isArray(d) ? d : (d?.items || d?.data || [])))
                .catch(() => {})
                .finally(() => setLoadingModels(false));
        }
    }, [isOpen, currentModel]);

    const handleModelSelect = (m: any) => {
        setSelectedModel(m);
        setSelectedTable(null);
        setPreviewData([]);
        setPreviewError(null);
    };

    const handleTableSelect = async (tableName: string) => {
        setSelectedTable(tableName);
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewData([]);
        try {
            if (!selectedModel?.id) {
                throw new Error('No data model selected');
            }

            const tableSql = quoteSqlIdentifier(tableName);
            const res = await interactiveFetch(`/datamodels/${selectedModel.id}/query`, null, {
                method: 'POST',
                body: JSON.stringify({ sql: `SELECT * FROM ${tableSql} LIMIT 50` })
            });
            setPreviewData(Array.isArray(res?.rows) ? res.rows : []);
        } catch (err: any) {
            setPreviewError(err.message || 'Failed to fetch preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    if (!isOpen) return null;

    const filteredModels = models.filter(m => !search || m.name?.toLowerCase().includes(search.toLowerCase()));
    const tables = selectedModel?.tables || selectedModel?.model_definition?.tables || [];

    const modalContent = (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 sm:p-8" onClick={onClose} style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[1000px] h-[80vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl">
                            <Database className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-[16px] font-bold text-slate-900 dark:text-slate-100 leading-none">Select Data Model</h2>
                            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1">Choose a data model and preview its tables before applying.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Left Panel: Models & Tables */}
                    <div className="w-[320px] flex flex-col border-r border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 shrink-0">
                        {/* Selected Model View */}
                        {selectedModel ? (
                            <div className="flex-1 flex flex-col min-h-0">
                                <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/50">
                                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Selected Model</div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Database className="w-4 h-4 text-blue-500 shrink-0" />
                                            <span className="text-[14px] font-bold text-slate-900 dark:text-slate-100 truncate">{selectedModel.name}</span>
                                        </div>
                                        <button onClick={() => setSelectedModel(null)} className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 hover:underline px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20">Change</button>
                                    </div>
                                </div>
                                <div className="p-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-100/50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-800">
                                    Tables ({tables.length})
                                </div>
                                <div className="flex-1 overflow-y-auto p-2">
                                    {tables.map((t: any, i: number) => {
                                        const tName = typeof t === 'string' ? t : t.name;
                                        const isSelected = selectedTable === tName;
                                        return (
                                            <button key={i} type="button" onClick={() => handleTableSelect(tName)}
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-left transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-semibold' : 'hover:bg-slate-200/50 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-300'}`}>
                                                <TableIcon className="w-4 h-4 shrink-0 opacity-70" />
                                                <span className="truncate flex-1 text-[13px]">{tName}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            /* Models List View */
                            <div id="dashboard-data-model-select" className="flex-1 flex flex-col min-h-0">
                                <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/50">
                                    <input type="text" placeholder="Search models..." value={search} onChange={e => setSearch(e.target.value)}
                                        className="w-full px-3 py-2 text-[13px] border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400" />
                                </div>
                                <div className="flex-1 overflow-y-auto p-2">
                                    {loadingModels ? (
                                        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                                    ) : filteredModels.length === 0 ? (
                                        <div className="py-8 text-center text-[12px] text-slate-500 italic">No models found.</div>
                                    ) : filteredModels.map(m => (
                                        <button key={m.id} type="button" onClick={() => handleModelSelect(m)}
                                            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 text-left hover:bg-slate-200/50 dark:hover:bg-slate-800/50 transition-colors">
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                                                <Database className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">{m.name}</div>
                                                <div className="text-[11px] text-slate-500 truncate">{m.description || m.storage_mode || 'Data Model'}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Panel: Data Preview */}
                    <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-950">
                        {selectedTable ? (
                            <div className="flex-1 flex flex-col min-h-0">
                                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0 bg-slate-50/30 dark:bg-slate-900/30">
                                    <div className="flex items-center gap-2 text-[14px] font-semibold text-slate-900 dark:text-slate-100">
                                        <TableIcon className="w-4 h-4 text-slate-400" /> {selectedTable}
                                    </div>
                                    <div className="text-[11px] text-slate-500 font-medium tracking-wide uppercase">Top 50 Rows</div>
                                </div>
                                <div className="flex-1 overflow-auto p-4">
                                    {previewLoading ? (
                                        <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
                                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                            <div className="text-[13px]">Fetching preview...</div>
                                        </div>
                                    ) : previewError ? (
                                        <div className="h-full flex items-center justify-center text-red-500 text-[13px] px-8 text-center">{previewError}</div>
                                    ) : previewData.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-slate-500 text-[13px]">No data found in this table.</div>
                                    ) : (
                                        <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden shadow-sm inline-block min-w-full">
                                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-sm border-collapse">
                                                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                                                    <tr>
                                                        {Object.keys(previewData[0] || {}).map(col => (
                                                            <th key={col} className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-widest text-[11px] whitespace-nowrap bg-slate-50 dark:bg-slate-900">
                                                                {col}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40 bg-white dark:bg-slate-950">
                                                    {previewData.map((row, i) => (
                                                        <tr key={i} className="hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors">
                                                            {Object.keys(row || {}).map(col => (
                                                                <td key={col} className="px-4 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px] text-[12px]" title={String(row[col] ?? '')}>
                                                                    {String(row[col] ?? '')}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-4 p-8 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                    <TableIcon className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                                </div>
                                <div>
                                    <div className="text-[14px] font-semibold text-slate-700 dark:text-slate-300 mb-1">No Table Selected</div>
                                    <div className="text-[12px] text-slate-500 max-w-[250px]">Select a table from the left panel to preview its data here.</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between shrink-0">
                    <div className="text-[12px] text-slate-500">
                        {selectedModel ? <span>Selected: <span className="font-bold text-slate-900 dark:text-slate-100">{selectedModel.name}</span></span> : 'No model selected'}
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-[12px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors">Cancel</button>
                        <button id="dashboard-data-model-confirm" onClick={() => selectedModel && onConfirm(selectedModel)} disabled={!selectedModel} className="px-5 py-2 text-[12px] font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors flex items-center gap-2">
                            <Check className="w-4 h-4" /> Confirm Selection
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
    return createPortal(modalContent, document.body);
};
